import React, { startTransition } from 'react'
import { Button, Menu } from '@asad/ui'
import { t } from 'i18next'
import { Icon } from '@app/components/icon'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks'
import { doLogout } from '@app/store/slices/auth-slice'
import clsx from 'clsx'
import fud24Logo from '@app/assets/img/fud_24_logo.png'

type MainMenuProps = {
  availableDevices: {
    title: string
    devices:
      | {
          text: string
          path: string
          id: string
        }[]
      | undefined
    icon: JSX.Element
  }[]
  mobile?: boolean
}

const MainMenu = ({ availableDevices, mobile = false }: MainMenuProps) => {
  const profile = useAppSelector((state) => state.profile?.data)
  const userRole = profile?.role || ['USER']
  const hasAdminRodo = userRole.includes('ADMIN_RODO')
  const dispatch = useAppDispatch()

  const menuItems: {
    title: string
    path?: string
    icon: React.ReactNode
    role: string[]
    subItems?: {
      title: string
      path?: string
      icon?: React.ReactNode
      devices?: { text: string; path: string; id: string }[]
    }[]
  }[] = [
    {
      title: t('common.dashboard'),
      path: '/dashboard',
      icon: <Icon name='House' color='white' />,
      role: ['ADMIN', 'USER'],
    },
    {
      title: t('common.administration'),
      role: ['ADMIN'],
      subItems: [
        { title: t('user.users'), path: 'admin/users', icon: <Icon name='Users' color='white' /> },
        { title: t('firm.firms'), path: 'admin/firms', icon: <Icon name='Factory' color='white' /> },
        { title: t('device.devices'), path: 'admin/devices', icon: <Icon name='Boxes' color='white' /> },
        // Feature temporarily disabled - Will be enable and implemented at version 2.0
        // { title: t('device.parts'), path: 'admin/parts', icon: <Icon name='Wrench' color='white' /> },
        {
          title: t('common.invertersAndAlarms'),
          path: 'admin/inverters-and-alarms',
          icon: <Icon name='BellRing' color='white' />,
        },
        {
          title: t('common.notifications'),
          path: 'admin/notifications',
          icon: <Icon name='Megaphone' color='white' />,
        },
        { title: t('wizard.wizard'), path: 'admin/wizard', icon: <Icon name='WandSparkles' color='white' /> },
        // Feature temporarily disabled - Will be enable and implemented at version 2.0
        // { title: t('offerRequests.title'), path: 'admin/offer-requests', icon: <Icon name='FileText' color='white' /> },
      ],
      icon: <Icon name='User' color='white' />,
    },
    {
      title: t('rodo.userConsents'),
      path: '/rodo/users-consents',
      icon: <Icon name='Users' color='white' />,
      role: ['ADMIN_RODO'],
    },
    {
      title: t('rodo.documents'),
      path: '/rodo/documents',
      icon: <Icon name='BookOpenText' color='white' />,
      role: ['ADMIN_RODO'],
    },
    {
      title: t('selectUserDetails.devices'),
      role: ['ADMIN', 'USER'],
      subItems: availableDevices,
      icon: <Icon name='Cpu' color='white' />,
    },
    {
      title: t('common.profile'),
      role: ['ADMIN', 'USER'],
      path: '/profile',
      icon: <Icon name='User' color='white' />,
    },
  ]

  const navigate = useNavigate()

  const effectiveRoles = hasAdminRodo ? ['ADMIN_RODO'] : userRole

  const filteredMenuItems = menuItems.filter(({ role }) => role.some((r) => effectiveRoles.includes(r)))

  const location = useLocation()
  const normalizedPath = location.pathname

  const menuItemsWithActive = filteredMenuItems.map((mi) => ({
    ...mi,
    isActive: !!mi.path && normalizedPath.startsWith(mi.path),
  }))

  const handleNavigate = (path: string) => {
    const target = path.startsWith('/') ? path : `/${path}`
    startTransition(() => {
      navigate(target)
    })
  }

  return (
    <div className='w-full flex flex-col items-center'>
      <div
        className={clsx(
          'flex flex-col justify-between gap-20 p-4 bg-background-wrapper-light dark:bg-background-wrapper-dark rounded-lg',
          {
            'h-214 w-64': !mobile,
            'w-full max-w-96': mobile,
          },
        )}
      >
        <div className='flex flex-col gap-2'>
          <Menu
            menuItems={menuItemsWithActive}
            handleNavigate={handleNavigate}
            nestedPaths={[t('selectUserDetails.devices')]}
          />
        </div>
        <div className='flex flex-col gap-4'>
          <a href='https://fud24.pl/' target='_blank'>
            <button className='h-full w-full p-3 border-transparent bg-background-general-light hover:bg-background-general-light-hover focus:ring-focused-text active:opacity-80 group flex flex-col gap-3 z-10 items-center justify-center rounded-md cursor-pointer px-4 ring-offset-background focus:outline-none focus-visible:ring-2 focus:ring-0 focus-visible:ring-offset-2 shadow-inner-button-light transition-all duration-200 dark:shadow-inner-button-dark'>
              <div className='flex justify-center'>
                <img src={fud24Logo} alt='Shopping Cart' />
              </div>
              <div className='flex items-center gap-2 self-start text-text-light'>
                <Icon name='ShoppingCart' />
                <span>{t('header.visitShoop')}</span>
              </div>
            </button>
          </a>
          <Button size='mdFull' icon={<Icon name='LogOut' color='white' />} onClick={() => dispatch(doLogout())}>
            {t('header.logout')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default MainMenu
